import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import {
  CredentialCategory as CredentialCategorySlug,
  CredentialType,
} from "@api/shared-runtime/enums";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import {
  credentialSectionElementId,
  summarizeCredentialAttention,
} from "../lib/credential-sections";
import type {
  Credential,
  CredentialCategory,
  CredentialDocument,
  CredentialVerification,
} from "../types";
import { CredentialTypeBlock } from "./credential-card/credential-type-block";
import { summaryState } from "./credential-card/credential-details/credential-summary-values";

const IDENTITY_CREDENTIAL_ORDER = [
  CredentialType.PersonalInfo,
  CredentialType.Npi,
];

function identityCredentialPriority(type: string): number {
  const priority = IDENTITY_CREDENTIAL_ORDER.findIndex(
    (credentialType) => credentialType === type,
  );
  return priority < 0 ? IDENTITY_CREDENTIAL_ORDER.length : priority;
}

/**
 * State licences are read as a list of states, so they follow the state name
 * each row leads with. A licence with no state (an IMLC letter of
 * qualification) or not yet held follows the named states; ties keep the
 * server's order because `Array.prototype.sort` is stable.
 */
function compareByStateName(left: Credential, right: Credential): number {
  const leftState = summaryState(left.version?.stateCode);
  const rightState = summaryState(right.version?.stateCode);
  if (!leftState || !rightState) {
    return Number(!leftState) - Number(!rightState);
  }
  return leftState.localeCompare(rightState);
}

/**
 * Instances grouped by credential type, keeping the server's order except for
 * state licences, which are alphabetical by state.
 *
 * The server sends one entry per holding, so a provider with four state
 * licences arrives as four flat entries. Grouping is what lets the type name be
 * written once instead of on every row.
 */
function byCredentialType(credentials: Credential[]) {
  const groups = new Map<string, Credential[]>();
  for (const credential of credentials) {
    const existing = groups.get(credential.credentialType);
    if (existing) {
      existing.push(credential);
    } else {
      groups.set(credential.credentialType, [credential]);
    }
  }
  groups.get(CredentialType.StateLicense)?.sort(compareByStateName);
  return [...groups.values()];
}

/**
 * What is outstanding in this section, in the words the rest of the page uses.
 *
 * One chip per kind rather than a single total: "4 need attention" is true of a
 * section holding four optional gaps and of one holding four expired licences,
 * and those are not the same morning's work. Severity leads — expired first,
 * then missing, then expiring — so the worst thing in the section is the first
 * thing read, and the colours match the header's counters.
 */
function groupSeverityBadge(
  credentials: Credential[],
  appearance: "default" | "panel",
) {
  const attention = summarizeCredentialAttention(credentials);
  const parts = [
    {
      count: attention.expired,
      label: "expired",
      classes: "bg-red-50 text-red-700",
    },
    {
      count: attention.missing,
      label: "missing",
      classes: "bg-red-50 text-red-700",
    },
    {
      count: attention.expiring,
      label: "expiring",
      classes: "bg-amber-50 text-amber-700",
    },
  ].filter((part) => part.count > 0);
  if (parts.length === 0) {
    return null;
  }

  return (
    <span
      className={cn(
        "ml-2 inline-flex flex-wrap items-center gap-1",
        appearance === "panel" && "ml-0",
      )}
    >
      {parts.map((part) => (
        <span
          key={part.label}
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
            part.classes,
          )}
        >
          {part.count} {part.label}
        </span>
      ))}
    </span>
  );
}

/** A collapsible category section — expansion is owned by the page so the
 * section nav can open a collapsed section when jumping to it. The wrapper
 * carries the shared anchor id (and scroll margin to clear the sticky mobile
 * category rail) that both scroll-to and scroll-spy target.
 * `category` is one entry of the server's `categories[]` render-order list. */
export function CredentialGroup({
  category,
  credentials,
  userId,
  readOnly = false,
  onEditCredential,
  onOpenDocument,
  onOpenVerificationEvidence,
  isOpeningDocument,
  onRemoveDocument,
  isExpanded,
  onExpandedChange,
  className,
  intro,
  children,
  appearance = "default",
}: {
  category: CredentialCategory;
  credentials: Credential[];
  userId: string;
  readOnly?: boolean;
  onEditCredential?: (credential: Credential) => void;
  onOpenDocument?: (document: CredentialDocument) => void;
  onOpenVerificationEvidence?: (verification: CredentialVerification) => void;
  isOpeningDocument?: boolean;
  onRemoveDocument?: (input: {
    credential: Credential;
    document: CredentialDocument;
  }) => void;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  className?: string;
  intro?: ReactNode;
  /** Optional grouped presentation supplied by the reader of this section. */
  children?: ReactNode;
  appearance?: "default" | "panel";
}) {
  if (credentials.length === 0 && !intro && !children) {
    return null;
  }

  const typeGroups = byCredentialType(credentials);
  if (category.slug === CredentialCategorySlug.Identity) {
    // Lead with the provider's name and NPI, retaining the order of other IDs.
    typeGroups.sort(
      (left, right) =>
        identityCredentialPriority(left[0].credentialType) -
        identityCredentialPriority(right[0].credentialType),
    );
  }

  return (
    <Accordion
      type="single"
      collapsible
      value={isExpanded ? category.slug : ""}
      onValueChange={(value) => onExpandedChange(value === category.slug)}
      id={credentialSectionElementId(category.slug)}
      className={cn(
        "scroll-mt-20 rounded-2xl border border-slate-200 bg-white shadow-xs lg:scroll-mt-4",
        className,
      )}
    >
      <AccordionItem value={category.slug} className="border-none">
        <AccordionTrigger
          className={cn(
            "rounded-2xl px-4 py-4 text-left hover:bg-slate-50 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:px-5",
            appearance === "panel" &&
              "gap-3 px-4 py-3 sm:px-4 [&[data-state=open]]:rounded-b-none [&[data-state=open]]:bg-slate-50",
          )}
        >
          <span
            className={cn(
              "flex min-w-0 flex-wrap items-center gap-y-2 text-base font-semibold text-slate-900",
              appearance === "panel" &&
                "flex-1 flex-col items-stretch gap-y-1.5 text-sm",
            )}
          >
            {appearance === "panel" ? (
              <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <span>{category.label}</span>
                <span className="whitespace-nowrap text-xs font-normal text-slate-600">
                  {
                    credentials.filter((credential) => credential.present)
                      .length
                  }{" "}
                  on file
                </span>
              </span>
            ) : (
              category.label
            )}
            {groupSeverityBadge(credentials, appearance)}
          </span>
          {/* A direct `svg` child of the trigger, which is what the shared
              component's `[&[data-state=open]>svg]:rotate-180` turns — so open
              points up, closed points down, with no state of its own to keep in
              step. Added here rather than by uncommenting the icon in
              `ui/accordion`, which would also change the shift-schedule
              accordion in a change about credentials.

              Decorative: Radix already puts `aria-expanded` on the trigger, so
              a screen reader is told the state without this. */}
          <ChevronDown
            className={cn(
              "ml-3 h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200",
              appearance === "panel" &&
                "ml-0 size-7 rounded-full bg-slate-100 p-1.5 text-slate-600",
            )}
            aria-hidden="true"
          />
        </AccordionTrigger>
        <AccordionContent
          className={cn(
            "px-4 pb-4 sm:px-5",
            appearance === "panel" && "pt-3 sm:px-4",
          )}
        >
          {intro && <div className="mb-4">{intro}</div>}
          {children ?? (
            <div className="divide-y divide-slate-100">
              {typeGroups.map((instances) => (
                <CredentialTypeBlock
                  key={instances[0].credentialType}
                  credentials={instances}
                  appearance={appearance}
                  userId={userId}
                  readOnly={readOnly}
                  onEditCredential={onEditCredential}
                  onOpenDocument={onOpenDocument}
                  onOpenVerificationEvidence={onOpenVerificationEvidence}
                  isOpeningDocument={isOpeningDocument}
                  onRemoveDocument={onRemoveDocument}
                />
              ))}
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
